import { app } from '../core/App';
import { DebugInput } from '../core/modules/DebugInput';
import { MatterPhysicsUpdater } from '../core/modules/MatterPhysicsUpdater';
import { ParallaxController } from '../core/modules/Parallax/ParallaxController';
import { GameDataHandler } from '../GameDataHandler';
import { Player } from '../Player';
import { PlayerController } from '../PlayerController';
import { LevelManager } from './LevelManager';
import { RunSettings, RunSettingsHandler } from './RunSettingsHandler';
import { getDeathEffectAudioFromWorld, getDeathEffectFromWorld, getNextWorld } from './worlds';
import { World, WorldName } from './worlds/world';

export class RunManager {
  public static events = {
    PlayerDeath: 'playerDeath',
    WorldSwitchStarted: 'worldSwitchStarted',
    WorldSwitchEnded: 'worldSwitchEnded',
    RunOver: 'runOver',
    ShowMessage: 'showMessage',
    HideMessage: 'hideMessage'
  };

  protected scene: Phaser.Scene;
  protected instantRestart: boolean;
  protected regenOnRestart: boolean;
  protected isInitialised: boolean;
  protected levelManager: LevelManager | undefined;
  protected parallaxController: ParallaxController | undefined;
  protected runSettingsHandler: RunSettingsHandler | undefined;
  protected camera: Phaser.Cameras.Scene2D.Camera | undefined;
  protected player: Player | undefined;
  private playerController: PlayerController | undefined;
  protected world: World | undefined;

  protected hasStartedRun: boolean;
  protected isRunning: boolean;
  protected startLineX: number;
  protected endLineX: number;
  // protected distanceTravelled: number;

  /** An amount in pixels of chunk distance to spawn initially. */
  protected initialDistanceToGenerate: number;
  /** An amount in pixels of chunk distance to keep ahead of the player. */
  protected distanceToGenerate: number;
  protected shiftDistance: number;
  protected particles!: Phaser.GameObjects.Particles.ParticleEmitterManager;

  protected endBonusCount:number = 10;

  constructor(scene: Phaser.Scene, instantRestart: boolean, regenOnRestart: boolean) {
    this.scene = scene;
    this.instantRestart = instantRestart;
    this.regenOnRestart = regenOnRestart;
    this.isInitialised = false;

    this.hasStartedRun = false;
    this.isRunning = false;
    this.startLineX = 0;
    this.endLineX = 0;
    // this.distanceTravelled = 0;

    this.initialDistanceToGenerate = 3000;
    this.distanceToGenerate = 3000;
    this.shiftDistance = 100000;
  }

  get RemainingRunDistance() {
    return (this.levelManager?.FurthestRightChunkX ?? 0) - this.PlayerDistance;
  }

  get PlayerDistance() {
    return (this?.player?.x ?? 0) - this.startLineX;
  }

  get Player() {
    return this.player;
  }

  public init(levelManager: LevelManager, parallaxController: ParallaxController) {
    this.levelManager = levelManager;
    this.parallaxController = parallaxController;

    this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.on(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);

    if (this.isInitialised) return;

    this.isInitialised = true;
  }

  public initRun(
    runSettings: RunSettings,
    camera: Phaser.Cameras.Scene2D.Camera,
    physicsUpdater: MatterPhysicsUpdater,
    playerLayer: Phaser.GameObjects.Layer,
    playerDeathLayer: Phaser.GameObjects.Layer,
    world: World
  ) {
    this.runSettingsHandler = new RunSettingsHandler(this.scene, runSettings);
    this.camera = camera;
    // this.player = player;
    this.world = world;

    this.instantRestart = runSettings.instantRestart;
    this.regenOnRestart = runSettings.regenOnRestart;

    this.player = new Player(this.scene, 0, 0, 0.5, playerLayer, playerDeathLayer);
    playerLayer.add(this.player);
    this.playerController = new PlayerController(this.scene, this.player);
    physicsUpdater.add(this.player.physicsUpdate, this.player, 0);

    this.setupWorld(world);

    this.resetRun();
  }

  public resetRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    let spawnPointX = this.world?.playerSpawnPoint.x ?? 0;
    let spawnPointY = this.world?.playerSpawnPoint.y ?? 0;

    this.startLineX = spawnPointX;
    this.updateFinishLine();

    this.player?.resetPosition(spawnPointX, spawnPointY);
  }

  public startRun() {
    this.hasStartedRun = true;
    this.isRunning = true;
    this.player?.startMoving();


  }

  public pauseRun() {
    this.isRunning = false;
    this.player?.stopMoving();
  }

  public resumeRun() {
    this.isRunning = true;
    this.player?.startMoving();
  }

  public endRun() {
    this.hasStartedRun = false;
    this.isRunning = false;

    if (this.instantRestart && this.player && this.world) {
      if (this.regenOnRestart) {
        this.levelManager?.endLevel();
        this.levelManager?.setupLevel(this.world.chunkStartX, this.world.chunkStartY, this.initialDistanceToGenerate);
        this.player?.respawn(this.world?.playerSpawnPoint.x ?? 0, this.world?.playerSpawnPoint.y ?? 0);
        this.camera?.startFollow(this.player, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      } else {
        this.levelManager?.resetLevel();
        this.player?.respawn(this.world?.playerSpawnPoint.x ?? 0, this.world?.playerSpawnPoint.y ?? 0);
        this.camera?.startFollow(this.player, true, 0.9, 0.5, -400, 0);
        this.scene.time.delayedCall(1000, () => this.startRun());
      }
    } else {
      this.levelManager?.endLevel();
      // TODO: Tween level off rather than delete.

      // Show endscreen.
      this.scene.events.emit(RunManager.events.RunOver);
    }
  }

  protected update(time: number, delta: number) {
    if (!this.hasStartedRun || !this.isRunning) return;

    if (this.player && this.player.IsAlive && this.world && this.player.y >= this.world.deathFloorY) {
      console.log('player Died', this.player.y, this.world.deathFloorY);
      console.log(`You made it ${this.PlayerDistance} pixels!`);

      this.onPlayerDeath(true);
      return;
    }

    this.levelManager?.despawnChunksBehind(this.camera?.worldView.left ?? 0, this.distanceToGenerate);
    if (this.levelManager?.spawnChunksAhead(this.camera?.worldView.right ?? 0, this.distanceToGenerate)) {
      this.updateFinishLine();
    }

    if (this.player && this.player.x >= this.shiftDistance) {
      this.shiftRunToZero();
    }

    if (this.player && this.player.x >= this.endLineX) {
      this.pauseRun();
      RunSettingsHandler.worldComplete(this.scene);

      if (this.isEndOfRush()) {
        RunSettingsHandler.rushComplete(this.scene);
        this.increaseDifficulty();
        this.endRushEffect();
      } else {
        this.transitionToNewWorld(getNextWorld(this.world));
      }
    }
  }

  protected shutdown() {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.off(RunManager.events.PlayerDeath, this.onPlayerDeath, this);
    // this.isInitialised = false;
  }

  protected setupWorld(newWorld: World) {
    this.world = newWorld;

    if (this.player && this.camera) {
      let playerCameraOffset = -400;

      this.parallaxController?.clearLayers();
      this.parallaxController?.buildLayersFromData(
        this.world.parallaxLayers.map((data) => {
          return { ...data };
        })
      );

      this.levelManager?.changeWorld(newWorld);
      this.levelManager?.setupLevel(this.world.chunkStartX, this.world.chunkStartY, this.initialDistanceToGenerate);

      this.player.resetPosition(this.world.playerSpawnPoint.x, this.world.playerSpawnPoint.y);

      // Moving the camera instantly to avoid jarring lerp jump.
      this.camera.setScroll(this.world.playerSpawnPoint.x - playerCameraOffset, this.world.playerSpawnPoint.y);
      this.camera.startFollow(this.player, true, 0.9, 0.5, playerCameraOffset, 0);

      this.updateFinishLine();
    }
  }

  protected transitionToNewWorld(newWorld: World) {
    console.log('Switching World');
    GameDataHandler.unlockWorld(this.scene, newWorld.id);
    this.scene.events.emit(RunManager.events.WorldSwitchStarted, [newWorld.id]);
    this.scene.time.delayedCall(500, this.switchWorld, [newWorld], this);
    this.scene.time.delayedCall(1000, this.resumeRun, [newWorld.id], this);
  }

  protected switchWorld(newWorld: World) {
    this.levelManager?.endLevel();
    this.setupWorld(newWorld);
    this.scene.events.emit(RunManager.events.WorldSwitchEnded, [newWorld.id]);
  }

  protected shiftRunToZero() {
    if (!this.levelManager) return;

    console.log('%cShifting to zero!!!', 'background: #000; color: #00ffff');
    this.parallaxController?.freezePosition();

    // TODO: Rather than move the start of the chunk to 0 offset the the chunk position by how far the player is into the chunk.
    // Find chunk player in. (player.x between chunkLeftX and chunkRightX).
    // offset = player.x - chunkLeftX.
    let offset = this.levelManager.shiftLevel(0);

    this.player?.shiftPosition(this.player.x - offset, this.player.y);
    this.camera?.setScroll(this.camera.scrollX - offset, this.camera.scrollY);
    this.updateFinishLine();

    this.parallaxController?.unfreezePosition();
  }

  protected updateFinishLine() {
    this.endLineX = this.levelManager?.LevelEndX ?? 0;
    this.camera?.setBounds(-1000, -10000, (this.levelManager?.FurthestRightChunkX ?? 0) + 1000, 10000 + 343 * 1);
  }

  protected onPlayerDeath(hitDeathFloor: boolean = false) {
    this.camera?.stopFollow();

    if (hitDeathFloor && this.player && this.world) {
      let spine = this.scene.add.spine(this.player.x, this.player.y, 'death-effect', 'animation', false);
      spine.preMultipliedAlpha = true;
      spine.setScale(0.5);
      spine.setSkinByName(getDeathEffectFromWorld(this.world.id));

      app.audioHandler.playSound(this.scene, getDeathEffectAudioFromWorld(this.world.id), 'sfx');
    }

    if (RunSettingsHandler.canContinueWithToken(this.scene) && this.player && this.levelManager && this.levelManager.Checkpoints.length > 0) {
      let bestX = 0;
      let bestY = 0;
      for (const point of this.levelManager.Checkpoints) {
        if (this.player.x < point.x) break;

        bestX = point.x;
        bestY = point.y;
      }

      if (bestX == 0) {
        bestX = this.levelManager.Checkpoints[0].x;
        bestY = this.levelManager.Checkpoints[0].y;
      }

      this.levelManager?.resetLevel();
      // this.player?.respawn(this.world?.playerSpawnPoint.x ?? 0, this.world?.playerSpawnPoint.y ?? 0);
      this.player?.respawn(bestX, bestY);
      this.camera?.startFollow(this.player, true, 0.9, 0.5, -400, 0);
      this.player.stopMoving();

      app.audioHandler.playSound(this.scene, 'SndRelicCollect', 'sfx');
      this.scene.time.delayedCall(1500, () => {
        this.player?.startMoving();
      });

      return;
    }

    this.player?.kill();
    this.scene.time.delayedCall(3000, () => {
      this.endRun();
    });
  }

  protected increaseDifficulty() {
    let newGlobalSpeed = RunSettingsHandler.increaseGlobalSpeed(this.scene, 0.15);
    // this.scene.matter.world.engine.timing.timeScale = newGlobalSpeed;
    console.log('Sped up', newGlobalSpeed);
  }

  protected isEndOfRush(): boolean {
    return this.world?.id == WorldName.Cave;
  }

  protected endRushEffect() {
    this.scene.events.emit(RunManager.events.ShowMessage, app.localeHandler.getLocalisedTextById('rushComplete', {useRTLFix: true}));

    console.log(this.particles);

      this.particles = this.scene.make.particles(
        {
          key: 'game',
          emitters: [
            {
              name: 'explode',
              on: false,
              frame: ['gem-blue.png', 'gem-green.png', 'gem-pink.png', 'coin.png', 'coin.png', 'coin.png'],
              // x: -960,
              // y: -400,
              angle: { min: 0, max: 360 },
              //scale: { min: 0.5, max: 1.5 },
              scale: { start: 0.4, end: 1.5, ease: Phaser.Math.Easing.Cubic.Out },
              alpha: { start: 1, end: 0, ease: Phaser.Math.Easing.Cubic.In },
              speed: { min: 300, max: 500 },
              gravityY: 450,
              lifespan: 5000,
              quantity: 1,
              emitZone: { source: new Phaser.Geom.Circle(0,0,50) }
            }
          ]
        },
        false
      );

    this.scene.add.existing(this.particles);
    let emitter = this.particles.emitters.getByName('explode');
    emitter?.setPosition(this.endLineX,0);
    emitter?.start();

    this.player?.celebrate();

    app.audioHandler.playSound(this.scene, 'SndEndSting', 'sfx');
    app.audioHandler.playSound(this.scene, 'SndEndCount', 'sfx');
    this.scene.time.delayedCall(3000, () => {
      //emitter?.stop();
      //this.particles.destroy();
      this.scene.events.emit(RunManager.events.HideMessage);
      this.transitionToNewWorld(getNextWorld(this.world));
    });

    this.scene.time.delayedCall(3500, () => {
      emitter?.stop();
      this.particles.destroy();

    });

    this.scene.time.delayedCall(4000, () => {
      this.scene.events.emit(RunManager.events.ShowMessage, app.localeHandler.getLocalisedTextById('rushStart', {useRTLFix: true}));
    });

    this.scene.time.delayedCall(7000, () => {
      this.scene.events.emit(RunManager.events.ShowMessage, app.localeHandler.getLocalisedTextById('rushStart2', {useRTLFix: true}));
    });

    this.scene.time.delayedCall(10000, () => {
      this.scene.events.emit(RunManager.events.HideMessage);
    });

    this.endBonusCount=10;
    this.endBonus();

  }

  protected endBonus(){
    this.scene.registry.inc(GameDataHandler.keys.Score, 5);
    this.endBonusCount--;
    if(this.endBonusCount>0){
      this.scene.time.delayedCall(300, () => {
        this.endBonus();
      });
    }

  }
}
